import {
  Box,
  Card,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Badge,
} from "@mui/material";
import DashboardStatsItem from "../components/dashboard-stats-item";
import CurrencyIcon from "@mui/icons-material/CurrencyBitcoin";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useState } from "react";
import { Dayjs } from "dayjs";
import { ServerRoutes } from "../routes";
import { Stats } from "../types";
import { DatePicker } from "@mui/x-date-pickers";
import { useQuery } from "@tanstack/react-query";
import { getToken } from "../utils";
import { useNavigate } from "react-router-dom";
import axios from "../utils/axios-instance";
import CourseChart from "../components/CourseChart";

const fetchSaleReport = async (
  fromDate?: Dayjs | null,
  toDate?: Dayjs | null,
) => {
  const query = new URLSearchParams();
  if (fromDate) query.append("from", fromDate.format("YYYY-MM-DD"));
  if (toDate) query.append("to", toDate.format("YYYY-MM-DD"));

  const response = await axios.get<Stats>(
    `${ServerRoutes.stats}?${query.toString()}`,
  );
  return response.data;
};

export const DashboardSection = () => {
  const navigate = useNavigate();
  if (!getToken()) navigate("/login");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);

  // Filter modal state
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [tempFromDate, setTempFromDate] = useState<Dayjs | null>(null);
  const [tempToDate, setTempToDate] = useState<Dayjs | null>(null);

  const { data: stats, refetch } = useQuery<Stats, Error>({
    queryKey: ["saleReport", fromDate?.format(), toDate?.format()],
    queryFn: () => fetchSaleReport(fromDate, toDate),
  });

  const openFilterModal = () => {
    setTempFromDate(fromDate);
    setTempToDate(toDate);
    setIsFilterModalOpen(true);
  };

  const closeFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const applyFilters = () => {
    setFromDate(tempFromDate);
    setToDate(tempToDate);
    closeFilterModal();
    refetch();
  };

  const clearFilters = () => {
    setTempFromDate(null);
    setTempToDate(null);
  };

  const hasActiveFilters = fromDate || toDate;

  // Calculate the number of active filters for badge
  const activeFilterCount = [fromDate ? 1 : 0, toDate ? 1 : 0].reduce(
    (sum, value) => sum + value,
    0,
  );

  // Handle removing filters directly from chips
  const handleRemoveFromDate = () => {
    setFromDate(null);
    refetch();
  };

  const handleRemoveToDate = () => {
    setToDate(null);
    refetch();
  };

  return (
    <Box>
      <Card
        sx={{
          p: { xs: 2, sm: 4 },
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          background: "white",
        }}
      >
        <Box display="flex" flexDirection="column" gap={3}>
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isMobile ? "flex-start" : "center"}
            gap={isMobile ? 2 : 0}
          >
            <Typography variant="h5" fontWeight="bold" color="text.secondary">
              Dashboard
            </Typography>
            <Badge
              badgeContent={activeFilterCount}
              color="primary"
              invisible={!hasActiveFilters}
              sx={{
                "& .MuiBadge-badge": {
                  fontWeight: "bold",
                },
              }}
            >
              <Button
                startIcon={<FilterListIcon />}
                variant={hasActiveFilters ? "contained" : "outlined"}
                color="primary"
                onClick={openFilterModal}
                sx={{
                  height: "40px",
                  borderRadius: 1.5,
                }}
              >
                Filters
              </Button>
            </Badge>
          </Box>

          {hasActiveFilters && (
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {fromDate && (
                <Chip
                  label={`From: ${fromDate.format("MMM DD, YYYY")}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                  onDelete={handleRemoveFromDate}
                />
              )}
              {toDate && (
                <Chip
                  label={`To: ${toDate.format("MMM DD, YYYY")}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                  onDelete={handleRemoveToDate}
                />
              )}
            </Stack>
          )}

          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            gap={2}
          >
            <DashboardStatsItem
              title="Total Sale"
              description={`${stats ? stats.totalSale : "loading..."}`}
              icon={CurrencyIcon}
            />
            <DashboardStatsItem
              title="Total Commission"
              description={`${stats ? stats.totalCommission : "loading..."}`}
              icon={CurrencyIcon}
            />
          </Box>
        </Box>

        {/* Filter Modal */}
        <Dialog
          open={isFilterModalOpen}
          onClose={closeFilterModal}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>
            Filter Dashboard
          </DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={3} py={1}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                fontWeight={600}
              >
                Date Range
              </Typography>
              <Box
                display="flex"
                gap={2}
                flexDirection={isMobile ? "column" : "row"}
              >
                <DatePicker
                  label="From Date"
                  value={tempFromDate}
                  onChange={(date) => setTempFromDate(date)}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
                <DatePicker
                  label="To Date"
                  value={tempToDate}
                  onChange={(date) => setTempToDate(date)}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={clearFilters} color="inherit">
              Clear All
            </Button>
            <Button onClick={closeFilterModal} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={applyFilters}
              variant="contained"
              color="primary"
              sx={{ borderRadius: 1.5 }}
            >
              Apply Filters
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
      <Box
        sx={{
          p: { xs: 2, sm: 4 },
          m: { xs: 2, sm: 4 },
          borderRadius: 2,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          background: "white",
        }}
      >
        <CourseChart courseId={1} />
      </Box>
    </Box>
  );
};
